<script setup lang="ts">
import device from '@@/bits/device'
import OzIcon from '@@/library/v4/components/OzIcon.vue'
import type { DashedLineMarginPercentFromViewportBottom } from '@@/pinia/surface_timeline_format_store'
import type { CSSProperties } from 'vue'
import { computed } from 'vue'

type Edge = 'top' | 'bottom' | 'start' | 'end'

const EDGE_TO_POSITIONAL_PROPERTY_MAP: Record<Edge, keyof CSSProperties> = {
  top: 'top',
  bottom: 'bottom',
  start: 'inset-inline-start',
  end: 'inset-inline-end',
}

const LINE_SIZE = 2 // px

const props = withDefaults(
  defineProps<{
    isMobileLandscapeScreen: boolean
    edge: Edge
    darkMode: boolean
    dashedLineMarginPercentFromViewportBottom: DashedLineMarginPercentFromViewportBottom
    gap?: number
  }>(),
  {
    gap: 0,
  },
)

const styles = computed<CSSProperties>(() => {
  const val = -(props.gap + LINE_SIZE) / 2
  const prop = EDGE_TO_POSITIONAL_PROPERTY_MAP[props.edge]
  return { [prop]: `${val}px` }
})
</script>

<template>
  <div :style="styles" :class="['h-full', 'absolute', 'z-drop-indicator', 'inset-y-0', 'w-0.5', 'pointer-events-none']">
    <div
      :class="[
        'absolute top-0 w-0.5 opacity-50',
        darkMode ? 'bg-canary-500' : 'bg-grape-500',
        // 30px is the circle position, 14px is the circle height, 2px is the gap between the circle and the line
        isMobileLandscapeScreen && ['h-[calc(100%-30px-14px-2px)]'],
        !isMobileLandscapeScreen && [
          // 148px/220px/186.5px is the circle position, 14px is the circle height, 2px is gap between the circle and the line
          'h-[calc(100%-148px-14px-2px)]',
          device.app && '@tablet-portrait/surface-container:h-[calc(100%-220px-14px-2px)]',
          !device.app && [
            dashedLineMarginPercentFromViewportBottom === 0.25 &&
              '@tablet-portrait/surface-container:h-[calc(100%-(100vh*0.25)-2px-14px-4px)]',
            dashedLineMarginPercentFromViewportBottom === 0.36 &&
              '@tablet-portrait/surface-container:h-[calc(100%-(100vh*0.36)-2px-14px-4px)]',
          ],
        ],
      ]"
    />
    <div
      :class="[
        'absolute -start-1.5',
        isMobileLandscapeScreen && ['bottom-[30px]'],
        !isMobileLandscapeScreen && [
          'bottom-[148px]',
          device.app && '@tablet-portrait/surface-container:bottom-[220px]',
          !device.app && [
            dashedLineMarginPercentFromViewportBottom === 0.25 &&
              '@tablet-portrait/surface-container:bottom-[calc(100vh*0.25+4px)]',
            dashedLineMarginPercentFromViewportBottom === 0.36 &&
              '@tablet-portrait/surface-container:bottom-[calc(100vh*0.36+4px)]',
          ],
        ],
      ]"
    >
      <OzIcon
        name="circle_donut_with_inner_circle"
        :class="[darkMode ? 'text-canary-500' : 'text-grape-500']"
        :size="14"
      />
    </div>
    <div
      :class="[
        'absolute bottom-0 w-0.5 opacity-50',
        darkMode ? 'bg-canary-500' : 'bg-grape-500',
        // 30px is the circle position, 2px is the gap between the circle and the line
        isMobileLandscapeScreen && ['h-[calc(30px-2px)]'],
        !isMobileLandscapeScreen && [
          // 148px/220px/186.5px is the circle position, 2px is the gap between the circle and the line
          'h-[calc(148px-2px)]',
          device.app && '@tablet-portrait/surface-container:h-[calc(220px-2px)]',
          !device.app && [
            dashedLineMarginPercentFromViewportBottom === 0.25 &&
              '@tablet-portrait/surface-container:h-[calc(100vh*0.25+2px)]',
            dashedLineMarginPercentFromViewportBottom === 0.36 &&
              '@tablet-portrait/surface-container:h-[calc(100vh*0.36+2px)]',
          ],
        ],
      ]"
    />
  </div>
</template>
